import { ref } from 'vue'
import type { AvailableVoices } from 'shared/src'
import { useConfiguratorApi } from '@/composables/api/useConfiguratorApi'

const availableVoices = ref<AvailableVoices>({})
const isLoadingVoices = ref(false)
const isInitialized = ref(false)

export const useAvailableVoices = () => {
  const { getAvailableVoices: fetchAvailableVoices } = useConfiguratorApi()

  const loadAvailableVoices = async (forceRefresh = false) => {
    if (isInitialized.value && !forceRefresh) {
      return availableVoices.value
    }

    if (isLoadingVoices.value) {
      await new Promise((resolve) => {
        const checkLoading = setInterval(() => {
          if (!isLoadingVoices.value) {
            clearInterval(checkLoading)
            resolve(availableVoices.value)
          }
        }, 50)
      })
      return availableVoices.value
    }

    isLoadingVoices.value = true
    try {
      const voices = await fetchAvailableVoices()
      availableVoices.value = voices
      isInitialized.value = true
      return voices
    } catch (error) {
      console.error('Failed to load available voices:', error)
      return {}
    } finally {
      isLoadingVoices.value = false
    }
  }

  const getVoiceOptionsForLang = (
    langCode: string,
  ): { label: string; value: string; flag: string }[] => {
    const normalizedCode = langCode.replace(/[^a-z]/g, '')
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const voices = availableVoices.value[normalizedCode] || []

    return [...voices]
      .sort((a, b) => {
        const flagA = a.flag ?? ''
        const flagB = b.flag ?? ''
        if (flagA !== flagB) {
          if (flagA === 'us') return -1
          if (flagB === 'us') return 1
          if (flagA === normalizedCode) return -1
          if (flagB === normalizedCode) return 1
          return flagA.localeCompare(flagB)
        }
        return a.name.localeCompare(b.name)
      })
      .map((voice) => ({
        label: voice.name,
        value: voice.provider_id,
        flag: voice.flag ?? '',
      }))
  }

  const getDefaultVoiceForLang = (langCode: string): string | undefined => {
    const normalizedCode = langCode.replace(/[^a-z]/g, '')
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const voices = availableVoices.value[normalizedCode] || []
    const defaultVoice = voices.find((v) => v.default)
    return defaultVoice?.provider_id
  }

  const getVoiceSamplePath = (langCode: string, voiceId: string): string => {
    const normalizedCode = langCode.replace(/[^a-z]/g, '')
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const voices = availableVoices.value[normalizedCode] || []
    const voice = voices.find((v) => v.provider_id === voiceId)
    return voice?.sample_path ?? ''
  }

  return {
    availableVoices,
    isLoadingVoices,
    isInitialized,
    loadAvailableVoices,
    getVoiceOptionsForLang,
    getDefaultVoiceForLang,
    getVoiceSamplePath,
  }
}
